import React, { useState, useEffect } from "react";
import { playgroundsApi } from "../api";
import "./PlaygroundContent.css";

interface PlaygroundContentProps {
  userPermissions: string[];
}

const PlaygroundContent: React.FC<PlaygroundContentProps> = ({ userPermissions }) => {
  const [availablePlaygrounds, setAvailablePlaygrounds] = useState<string[]>([]);
  const [loadingSoftwareList, setLoadingSoftwareList] = useState(true);
  const [playgroundUrl, setPlaygroundUrl] = useState<string | null>(null);
  const [isLoadingPlayground, setIsLoadingPlayground] = useState(false);
  const [playgroundLoadingMessage, setPlaygroundLoadingMessage] = useState('');
  const [hasAttemptedPlaygroundLoad, setHasAttemptedPlaygroundLoad] = useState(false);

  const playgroundLoadingMessages = [
    'Starting up your playground environment...',
    'Configuring software workspace...',
    'Setting up your personal instance...',
    'Preparing development environment...',
    'Almost ready! Finalizing setup...',
    'Loading your playground...',
    'Initializing software tools...',
    'Creating your workspace...',
  ];

  useEffect(() => {
    loadAvailablePlaygrounds();
  }, [userPermissions]);

  const loadAvailablePlaygrounds = async () => {
    setLoadingSoftwareList(true);
    try {
      const softwareList = await playgroundsApi.getSoftwareList();
      setAvailablePlaygrounds(softwareList);
    } catch (error) {
      console.error("Failed to load playground software list:", error);
    } finally {
      setLoadingSoftwareList(false);
    }
  };

  // Cycle through loading messages when loading playground
  useEffect(() => {
    if (isLoadingPlayground) {
      let messageIndex = 0;
      setPlaygroundLoadingMessage(playgroundLoadingMessages[0]);
      
      const interval = setInterval(() => {
        messageIndex = (messageIndex + 1) % playgroundLoadingMessages.length;
        setPlaygroundLoadingMessage(playgroundLoadingMessages[messageIndex]);
      }, 5000); // Change message every 5 seconds

      return () => clearInterval(interval);
    }
  }, [isLoadingPlayground]);

  const handleOpenPlayground = async (software: string) => {
    setIsLoadingPlayground(true);
    setHasAttemptedPlaygroundLoad(true);
    setPlaygroundLoadingMessage(playgroundLoadingMessages[0]);
    
    try {
      const playground = await playgroundsApi.createPlayground(software);
      if (playground) {
        setPlaygroundUrl(playground.access_url);
      } else {
        throw new Error("Failed to create playground");
      }
    } catch (error) {
      console.error('Failed to create playground:', error);
      alert('Failed to create playground. Please try again.');
    } finally {
      setIsLoadingPlayground(false);
    }
  };

  const handleResetPlayground = async (software: string) => {
    if (!window.confirm(`Are you sure you want to reset your ${software} playground? This will delete all your data and stop the current instance.`)) {
      return;
    }

    setIsLoadingPlayground(true);
    setPlaygroundLoadingMessage('Resetting your playground...');
    
    try {
      await playgroundsApi.deletePlayground(software);
      setPlaygroundUrl(null);
      await handleOpenPlayground(software);
    } catch (error) {
      console.error('Failed to reset playground:', error);
      alert('Failed to reset playground. Please try again.');
      setIsLoadingPlayground(false);
    }
  };

  const getPlaygroundSoftware = (): string | null => {
    return availablePlaygrounds[0] || null;
  };

  const getLoginInfo = (software: string): { description: string; username: string; password: string } | null => {
    switch (software.toLowerCase()) {
      case 'immich':
        return {
          description: 'A self-hosted photo and video backup solution',
          username: 'emmett.miller@gmail.com',
          password: 'password'
        };
      case 'odoo':
        return {
          description: 'An open-source business management software suite',
          username: 'admin',
          password: 'admin'
        };
      case 'erpnext':
        return {
          description: 'An open-source Enterprise Resource Planning (ERP) system',
          username: 'administrator',
          password: 'Jasdbj@!sa8aX2!d'
        };
      default:
        return null;
    }
  };

  const software = getPlaygroundSoftware();

  // Automatically load playground when component mounts and software is available
  useEffect(() => {
    if (software && !playgroundUrl && !isLoadingPlayground && !hasAttemptedPlaygroundLoad) {
      handleOpenPlayground(software);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [software, playgroundUrl, isLoadingPlayground, hasAttemptedPlaygroundLoad]);

  if (loadingSoftwareList) {
    return (
      <div className="playground-container">
        <div className="playground-loading">
          <span className="loading-spinner"></span>
          <span>Loading available playgrounds...</span>
        </div>
      </div>
    );
  }

  if (!software) {
    return (
      <div className="playground-container">
        <div className="playground-error">
          No playgrounds available for your permissions.
        </div>
      </div>
    );
  }

  return (
    <div className="playground-container">
      {isLoadingPlayground ? (
        <div className="playground-loading">
          <span className="loading-spinner"></span>
          <span>{playgroundLoadingMessage}</span>
          <p className="loading-note">This may take up to 5 minutes. Feel free to leave this page - your playground will continue loading in the background.</p>
        </div>
      ) : playgroundUrl ? (
        <div className="playground-button-container">
          <h2>Your {software.charAt(0).toUpperCase()}{software.slice(1)} Playground is Ready!</h2>
          {(() => {
            const loginInfo = getLoginInfo(software);
            return loginInfo ? (
              <div className="playground-info">
                <p>{loginInfo.description}</p>
                <div className="login-credentials">
                  <h3>Login Credentials:</h3>
                  <p><strong>Username:</strong> <code>{loginInfo.username}</code></p>
                  <p><strong>Password:</strong> <code>{loginInfo.password}</code></p>
                </div>
              </div>
            ) : null;
          })()}
          <div className="playground-buttons">
            <button
              className="playground-open-button"
              onClick={() => {
                  window.open(playgroundUrl, '_blank');
              }}
            >
              Open {software.charAt(0).toUpperCase()}{software.slice(1)} Playground
            </button>
            <button
              className="playground-reset-button"
              onClick={() => handleResetPlayground(software)}
            >
              Reset Playground
            </button>
          </div>
        </div>
      ) : hasAttemptedPlaygroundLoad ? (
        <div className="playground-error">
          Failed to load playground. Please try again.
        </div>
      ) : null}
    </div>
  );
};

export default PlaygroundContent;